import { AppShell, Box, MantineColor, MantineSize } from '@mantine/core';
import { useFavicon } from '@mantine/hooks';
import { memo, useCallback, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ClientsProvider } from 'src/clients/context/ClientsContext';
import {
  CurrentClientProvider,
  useCurrentClientIfAvailable,
} from 'src/clients/context/CurrentClientProvider';
import FactFindProvider from 'src/fact-find/FactFindProvider';
import AgendaProvider from 'src/meeting-agenda/AgendaProvider';
import MeetingNoteProvider from 'src/meeting-note/MeetingNoteProvider';
import SuitabilityProvider from 'src/suitability-report/SuitabilityProvider';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import Chat from './chat/Chat';
import Header, { HEADER_HEIGHT } from './header/Header';
import { UsersProvider } from 'src/admin/users/context/UsersContext';

type Props = {
  children?: React.ReactNode;
  showChat?: boolean;
  bg?: MantineColor;
  p?: MantineSize | number;
};

const DEFAULT_CHAT_SIZE = 350;
const MIN_WIDTH = 200;
const savedWidth = Number(localStorage.getItem('draggableWidth') || DEFAULT_CHAT_SIZE);
function DeprecatedAppContainer({ children, showChat = true, p = 'md', ...pageProps }: Props) {
  useFavicon('https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/smallLogo.svg');

  const [chatCollapsed, setChatCollapsed] = useState(!showChat);
  const [chatWidth, setChatWidth] = useState(
    savedWidth > MIN_WIDTH ? savedWidth : DEFAULT_CHAT_SIZE,
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStoredWidth = useRef<number | null>(null);
  const handleChatDrag = useCallback((newWidth: number) => {
    let targetWidth = newWidth;

    if (targetWidth < MIN_WIDTH) {
      setChatCollapsed(true);
      targetWidth = 200;
    }

    setChatWidth(targetWidth);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      localStorage.setItem('draggableWidth', targetWidth.toString());
      lastStoredWidth.current = targetWidth;
    }, 500);
  }, []);

  const content = children ? children : <Outlet />;

  const handleShowChat = useCallback(() => {
    setChatCollapsed(false);
  }, [setChatCollapsed]);

  const handleHideChat = useCallback(() => {
    setChatCollapsed(true);
  }, [setChatCollapsed]);

  return (
    <CurrentClientProvider>
      <ClientsProvider>
        <SuitabilityProvider>
          <FactFindProvider>
            <AgendaProvider>
              <MeetingNoteProvider>
                <UsersProvider>
                  <DeprecatedAfternoonAppShell
                    chatWidth={chatWidth}
                    chatCollapsed={chatCollapsed}
                    handleChatDrag={handleChatDrag}
                    handleShowChat={handleShowChat}
                    handleHideChat={handleHideChat}
                    pageProps={pageProps}
                    p={p}
                    content={content}
                  />
                </UsersProvider>
              </MeetingNoteProvider>
            </AgendaProvider>
          </FactFindProvider>
        </SuitabilityProvider>
      </ClientsProvider>
    </CurrentClientProvider>
  );
}

type AppShellProps = {
  chatWidth: number;
  chatCollapsed: boolean;
  handleChatDrag: (width: number) => void;
  handleShowChat: () => void;
  handleHideChat: () => void;
  p: MantineSize | number;
  pageProps: Omit<Props, 'children' | 'showChat'>;
  content: React.ReactNode;
};

const DeprecatedAfternoonAppShell = ({
  chatWidth,
  chatCollapsed,
  handleChatDrag,
  handleShowChat,
  handleHideChat,
  pageProps,
  content,
  p = 'md',
}: AppShellProps) => {
  const { clientId } = useCurrentClientIfAvailable();
  const width = clientId ? chatWidth : 0;
  return (
    <AppShell
      header={{
        height: HEADER_HEIGHT,
      }}
      aside={{
        width,
        breakpoint: 'md',
        collapsed: { desktop: chatCollapsed || chatWidth < 200, mobile: true },
      }}
    >
      <Header showChat={handleShowChat} isChatOpened={!chatCollapsed} />
      <AppShell.Main bg="contentBackground" {...pageProps}>
        <Box p={p}>
          <AppBreadcrumbs />
          {content}
        </Box>
      </AppShell.Main>
      <Chat clientId={clientId} width={width} onDrag={handleChatDrag} onHideChat={handleHideChat} />
    </AppShell>
  );
};

export default memo(DeprecatedAppContainer);
